import React, {Fragment, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import { Button, Icon } from "@axum/ax-design";
import { filters as filtersDrawer, view as viewLayout } from "@axum/ax-design/dist/js";
import { languageContext } from "../../../config/language";

// components
import Orders from "../Tabs/Orders/Orders.container";
import Legacy from "../Tabs/Legacy/Legacy.container";
import OrdersMassive from "../Tabs/retry/Orders.container";
import Budgets from "../Tabs/Budgets/Budgets.container";
import { OrdersGridPlaceholder } from "../../Common/Orders/OrdersGrid";
import PageKpis from "./PageKpis";
import ViewMenu from "./ViewMenu";
import PageTabs from "./PageTabs";
import { HeaderActionsContext, HeaderMenuAction } from "./HeaderActions";
import { AxumHostStyles, useAxumFrame, useHostTheme } from "./AxumHost";
import { PageHeader } from "./styles";
import { FiltersPanel, FiltersTrigger } from "../Filters/Filters.styles";

// types
import { DateRange } from "../../../types/common";
import {getFeaturesAllowed} from "../../../utils/commonFetchers";
import { useLoadErrorToasts } from "../../../utils/loadErrors";
import { useSessionReady } from "../../../config/session";
import { readViewSettings, saveViewSettings, ViewSettings } from "../../../config/viewSettings";
import { TableRoom, TableRoomContext } from "../../../config/tableRoom";
import { useCededFilters } from "./useCededFilters";
import { useShowRange } from "../../../config/showRange";

interface Props {
  // filters
  rangeDate: DateRange;
  activeFilters: any;
  pendingFilters: number;
  sendFilters: () => void;
  resetFilters: () => void;
  discardFilters: () => void;
  presetRange: (range: DateRange) => void;
  activeFiltersCount: number;
  filters: React.ReactNode;
  ordersTotal: number;
  fetchingOrders: boolean;
  sellersTotal: number;
  reintentosQuantity:number;
  pendienteDescargaQuantity:number;
}

const formatDay = (date: Date | null) => (date ? date.toLocaleDateString("es-AR") : "");

const rangeText = (range: DateRange) => {
  const start = formatDay(range.start);
  const end = formatDay(range.end);
  return start === end ? start : `${start} – ${end}`;
};

const pendingText = (count: number) => `${count} ${count === 1 ? "cambio" : "cambios"} sin aplicar`;

const filtersLayoutClass = (settings: ViewSettings, ceded: boolean) => {
  if (settings.floatingFilters || ceded) return " ax-view-floating-filters";
  return settings.filtersEnd ? " ax-view-filters-end" : "";
};

export default (props: Props) => {
  const { languageContext: lang } = useContext(languageContext);

  const [selectedPage, setSelectedPage] = useState(1);
  const [allowedTabs, setAllowedTabs] = useState<string[]>([]);
  const [featuresLoaded, setFeaturesLoaded] = useState(false);
  const sessionReady = useSessionReady();
  const [actionsHost, setActionsHost] = useState<HTMLDivElement | null>(null);
  const [menuActions, setMenuActions] = useState<{ id: string; action: HeaderMenuAction }[]>([]);
  const registerMenuAction = useCallback((id: string, action: HeaderMenuAction | null) => setMenuActions(current => [
    ...current.filter(entry => entry.id !== id),
    ...(action ? [{ id, action }] : []),
  ]), []);
  const headerSlots = useMemo(() => ({ host: actionsHost, registerMenuAction }), [actionsHost, registerMenuAction]);
  const [viewSettings, setViewSettings] = useState(readViewSettings);
  const filtersPanel = useRef<HTMLDivElement>(null);
  const viewRoot = useRef<HTMLDivElement>(null);
  const [tableRoom, setTableRoom] = useState<TableRoom | null>(null);
  const ceded = useCededFilters(viewRoot, viewSettings.floatingFilters, tableRoom);
  const showRange = useShowRange();
  useLoadErrorToasts();
  useAxumFrame();
  const theme = useHostTheme();

  useLayoutEffect(() => {
    if (viewRoot.current) viewLayout.measure(viewRoot.current);
  }, []);

  //NO TOCAR LA LA VARIABLE 'feature', NO CAMBIAR SU VALOR NI USARLA EN OTRO LADO
  const _tabs = [
    {
      page: 1,
      name: lang.ordersTab,
      entity: "orders",
      feature: "orders",
      component: (
        <Orders
          rangeDate={props.rangeDate}
          activeFilters={props.activeFilters}
          sendFilters={props.sendFilters}
          featuresAllowed={allowedTabs}
        />
      ),
    },
    {
      page: 2,
      name: lang.legacy,
      entity: "seller_stats",
      feature: "seller_stats",
      component: (
        <Legacy
          rangeDate={props.rangeDate}
          activeFilters={props.activeFilters}
          sendFilters={props.sendFilters}
          featuresAllowed={allowedTabs}
        />
      ),
    },
    {
      page: 3,
      name: lang.rejectedTab,
      entity: "rejected_orders",
      feature: "rejected_orders",
      component: (
        <OrdersMassive
          rangeDate={props.rangeDate}
          activeFilters={props.activeFilters}
          sendFilters={props.sendFilters}
          featuresAllowed={allowedTabs}
        />
      ),
    },
    {
      page: 4,
      name: lang.budgetsTab,
      entity: "budgets",
      feature: "budgets",
      component: (
        <Budgets
          rangeDate={props.rangeDate}
          activeFilters={props.activeFilters}
          sendFilters={props.sendFilters}
          featuresAllowed={allowedTabs}
        />
      ),
    }
  ];

  useEffect(() => {
    if (!sessionReady) return;
    getFeaturesAllowed()
        .then(xs => {
          setAllowedTabs(
            xs.filter(x => x.isAllowed)
              .map(x => x.feature)
          );
          setFeaturesLoaded(true);
        });
  }, [sessionReady]);

  const getFilteredTabs = () => _tabs.filter(x => allowedTabs.includes(x.feature));

  const tabCount = (entity: string) => {
    if (entity === "rejected_orders") return props.reintentosQuantity;
    if (entity === "budgets") return props.pendienteDescargaQuantity;
    return 0;
  };

  const tabs = getFilteredTabs();
  const currentTab = tabs.find(tab => tab.page === selectedPage);
  const tabOptions = tabs.map(tab => ({
    value: String(tab.page),
    label: <span data-cy={`NavTab-${tab.name.replace(/\s/g, '_')}`}>{tab.name}</span>,
    count: tabCount(tab.entity) || undefined,
  }));

  const tabOf = (entity: string) => tabs.find(tab => tab.entity === entity);

  // La pestaña que se monta pide sus datos con el rango que recibe: dos pedidos a la vez se pisan según cuál vuelva último.
  const showElsewhere = (entity: string, range: DateRange) => {
    const tab = tabOf(entity);
    if (!tab) return;
    if (tab.page === selectedPage) {
      showRange(range);
      return;
    }
    props.presetRange(range);
    setSelectedPage(tab.page);
  };

  const openFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (filtersPanel.current) filtersDrawer.show(filtersPanel.current, event.currentTarget);
  };
  const closeFilters = () => filtersDrawer.hide();
  const changeViewSettings = (settings: ViewSettings) => {
    saveViewSettings(settings);
    setViewSettings(settings);
  };
  const applyFilters = () => {
    props.sendFilters();
    closeFilters();
  };

  return (
      <HeaderActionsContext.Provider value={headerSlots}>
        <TableRoomContext.Provider value={setTableRoom}>
          <AxumHostStyles />
          <div ref={viewRoot} className={`ax-app ax-view ax-view-sticky ax-view-glass${filtersLayoutClass(viewSettings, ceded)}`} data-theme={theme}>
            <div className="ax-view-stack">
              <PageHeader className="ax-page-header">
                <div className="ax-page-header-bar">
                  <div className="ax-page-header-main ax-flex-wrap">
                    <div className="ax-page-header-titles">
                      <h1 className="ax-page-header-title">Pedidos 360</h1>
                      <p className="ax-page-header-subtitle"><Icon name="calendar" />{rangeText(props.rangeDate)}</p>
                    </div>
                    {tabOptions.length > 0 && (
                        <PageTabs
                            options={tabOptions}
                            value={String(selectedPage)}
                            onChange={value => setSelectedPage(Number(value))}
                        />
                    )}
                  </div>
                  <div className="ax-page-header-actions" ref={setActionsHost} />
                  <ViewMenu settings={viewSettings} onChange={changeViewSettings} actions={menuActions} />
                </div>
              </PageHeader>
              <PageKpis
                  hidden={!viewSettings.kpis}
                  entity={currentTab ? currentTab.entity : "orders"}
                  ordersTotal={props.ordersTotal}
                  sellersTotal={props.sellersTotal}
                  fetchingOrders={props.fetchingOrders}
                  reintentosQuantity={props.reintentosQuantity}
                  pendienteDescargaQuantity={props.pendienteDescargaQuantity}
                  range={props.rangeDate}
                  canShow={entity => Boolean(tabOf(entity))}
                  onShow={showElsewhere}
              />
            </div>

            <div className="ax-view-layout">
              <aside className="ax-view-sidebar">
                <div className="ax-filters-backdrop" onClick={closeFilters} />
                <FiltersPanel className="ax-filters" id="o360-filters" ref={filtersPanel} role="region" aria-labelledby="o360-filters-title">
                  <div className="ax-filters-header">
                    <span className="ax-filters-title" id="o360-filters-title">
                      <Icon name="filter" />Filtros
                      {props.activeFiltersCount > 0 && <span className="ax-filters-count">{props.activeFiltersCount}</span>}
                    </span>
                    <button type="button" className="ax-btn-icon ax-filters-clear" aria-label="Limpiar filtros"
                            data-ax-tooltip="Limpiar filtros" data-cy="ButtonResetFilters" onClick={props.resetFilters}>
                      <Icon name="broom" />
                    </button>
                    <button type="button" className="ax-btn-icon ax-filters-close" aria-label="Cerrar filtros" onClick={closeFilters}>
                      <Icon name="close" />
                    </button>
                  </div>
                  <div className="ax-filters-body">
                    {props.filters}
                  </div>
                  <div className="o360-filters-footer">
                    <div aria-live="polite">
                      {props.pendingFilters > 0 && (
                        <p className="o360-filters-pending">
                          {pendingText(props.pendingFilters)}
                          <Button variant="link" size="sm" data-cy="ButtonDiscardFilters" onClick={props.discardFilters}>Descartar</Button>
                        </p>
                      )}
                    </div>
                    <Button variant={props.pendingFilters > 0 ? "primary" : "soft-primary"} className="ax-w-100"
                            data-cy="ButtonFilter" onClick={applyFilters}>
                      Aplicar filtros
                    </Button>
                  </div>
                </FiltersPanel>
              </aside>

              <div className="ax-view-main">
                <div className="ax-datagrid-toolbar">
                  <FiltersTrigger type="button" className={`ax-filters-trigger${props.pendingFilters > 0 ? " is-pending" : ""}`}
                                  aria-controls="o360-filters" aria-expanded="false"
                                  data-ax-count={props.activeFiltersCount || undefined} onClick={openFilters}>
                    <Icon name="filter" />Filtros
                    {props.pendingFilters > 0 && <span className="ax-visually-hidden">, {pendingText(props.pendingFilters)}</span>}
                  </FiltersTrigger>
                </div>
                {!featuresLoaded && <OrdersGridPlaceholder />}
                {tabs
                    .map((tab, key) => {
                      return (
                          Boolean(tab.page === selectedPage) && (
                              <Fragment key={key}>{tab.component}</Fragment>
                          )
                      );
                    })
                }
              </div>
            </div>
          </div>
        </TableRoomContext.Provider>
      </HeaderActionsContext.Provider>
  );
};
